import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import useAppModernisation from "../../../hooks/useAppModernisation";
import styles from "./ContentMenuModal.module.scss";
import PageSidebar from "../PageSidebar";

/**
 * Generic content menu modal component that can be used for calculators, resources, etc.
 */
const ContentMenuModal = ({ isOpen, onClose, title, icon, menuItems }) => {
  const location = useLocation();
  const previousLocationRef = useRef(location.pathname);
  const appModernisation = useAppModernisation();
  const { isWebModernisationEnabled } = appModernisation || {};

  // Set CSS variable for AppSidebar width when modal opens
  useEffect(() => {
    if (isOpen) {
      const appSidebar = document.querySelector('[class*="appSidebar"]');
      if (appSidebar) {
        const appSidebarWidth = appSidebar.offsetWidth;
        document.documentElement.style.setProperty(
          "--app-sidebar-width",
          `${appSidebarWidth}px`,
        );
      }
    }
    return () => {
      // Clean up CSS variable when modal closes
      if (!isOpen) {
        document.documentElement.style.removeProperty("--app-sidebar-width");
      }
    };
  }, [isOpen]);

  // Close modal when location changes (navigation happens)
  useEffect(() => {
    if (isOpen && location.pathname !== previousLocationRef.current) {
      onClose();
    }
    previousLocationRef.current = location.pathname;
  }, [location.pathname, isOpen, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <div
      className={`${styles.modalOverlay} ${isWebModernisationEnabled ? "modernisation-enabled" : ""}`}
      onClick={handleOverlayClick}
    >
      <div className={styles.sidebarContainer}>
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label={`Close ${title.toLowerCase()} menu`}
        >
          <i className="tg-icons-Cross"></i>
        </button>

        <PageSidebar title={title} icon={icon} menuItems={menuItems} />
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

ContentMenuModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      path: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

export default ContentMenuModal;
